import DealsWidgetBase from "./deals_widget_base";
import iconCrossSvg from "../assets/icon__cross.svg";

export const DealsWidgetPanelConfig = {
  componentTypeKey: "DealsWidgetPanel",
  baseHtmlClass: "deals_widget__panel",
  selector: ".deals_widget__panel",

  contentSelector: ".deals_widget__panel_content",
  closeSelector: ".deals_widget__panel_close",
};

export default class DealsWidgetPanel extends DealsWidgetBase {
  static get config() {
    return DealsWidgetPanelConfig;
  }

  constructor(parentComponent) {
    const element = DealsWidgetPanel.buildElement();

    super(element, parentComponent, DealsWidgetPanel.config);

    // Get sub-elements
    this.contentElement = this.element.querySelector(DealsWidgetPanelConfig.contentSelector);
    this.closeElement = this.element.querySelector(DealsWidgetPanelConfig.closeSelector);

    // Container reference for width animation
    this.containerElement = parentComponent.element;

    // Track closing animation state
    this.isClosing = false;
    this.wasOpen = false;

    // Store trigger-only width for close animation
    this.triggerOnlyWidth = null;

    // Bind handlers
    this.handleCloseClick = this.handleCloseClick.bind(this);

    // Attach close button listener
    this.closeElement.addEventListener("click", this.handleCloseClick);

    // Insert before trigger so panel appears above it in flex column
    parentComponent.element.insertBefore(this.element, parentComponent.triggerElement);
  }

  static buildElement() {
    const element = document.createElement("div");
    element.classList.add(DealsWidgetPanelConfig.baseHtmlClass);

    // Close button (floats in top right)
    const closeButton = document.createElement("button");
    closeButton.classList.add("deals_widget__panel_close");
    closeButton.innerHTML = iconCrossSvg;
    closeButton.setAttribute("aria-label", "Close deals panel");
    element.appendChild(closeButton);

    // Content
    const content = document.createElement("div");
    content.classList.add("deals_widget__panel_content");
    element.appendChild(content);

    return element;
  }

  handleCloseClick(event) {
    event.preventDefault();
    event.stopPropagation();
    this.dispatchAction("close");
  }

  setContent(html) {
    this.contentElement.innerHTML = html;
  }

  render() {
    const isOpen = this.readStateMachineValue("isOpen");
    const isLoading = this.readStateMachineValue("isLoading");
    const isError = this.readStateMachineValue("isError");
    const hasData = this.readStateMachineValue("hasData");

    // Panel only shows when open AND has data (or error)
    // During loading, the spinner shows instead
    const shouldShowPanel = isOpen && (hasData || isError);

    // Handle open/close state with closing animation
    if (shouldShowPanel) {
      // Opening - has data ready to display
      if (!this.wasOpen) {
        this.animateWidthOpen();
      }
      this.isClosing = false;
      this.removeHtmlClassState("closing");
      this.removeHtmlClassState("closed");
      this.addHtmlClassState("open");
    } else if (this.wasOpen && !this.isClosing) {
      // Animate container width closed
      this.animateWidthClose();
      // Transitioning from open to closed - play closing animation
      this.isClosing = true;
      this.removeHtmlClassState("open");
      this.removeHtmlClassState("closed");
      this.addHtmlClassState("closing");

      // After animation completes, switch to closed
      this.element.addEventListener("animationend", () => {
        this.isClosing = false;
        this.removeHtmlClassState("closing");
        this.addHtmlClassState("closed");
      }, { once: true });
    } else if (!this.isClosing) {
      // Already closed or initial state
      this.removeHtmlClassState("open");
      this.removeHtmlClassState("closing");
      this.addHtmlClassState("closed");
    }

    this.wasOpen = shouldShowPanel;

    this.conditionallySetHtmlClassState(isLoading, "loading");
    this.conditionallySetHtmlClassState(isError, "error");
    this.conditionallySetHtmlClassState(hasData, "has_data");

    // Render content based on state
    this.renderContent();
  }

  renderContent() {
    const isError = this.readStateMachineValue("isError");
    const errorMessage = this.readStateMachineValue("errorMessage");

    // Panel only shows error or success content
    // Loading is handled by the spinner component
    if (isError) {
      this.contentElement.innerHTML = this.buildErrorHtml(errorMessage);
      // Attach retry handler
      const retryButton = this.contentElement.querySelector(".deals_widget__retry_button");
      if (retryButton) {
        retryButton.addEventListener("click", (e) => {
          e.preventDefault();
          this.parentComponent.retry();
        });
      }
    }
    // Success content is set directly via setContent()
  }

  buildErrorHtml(message) {
    return `
      <div class="deals_widget__error">
        <p>${message || "Unable to load deals. Please try again."}</p>
        <button class="deals_widget__retry_button">Try again</button>
      </div>
    `;
  }

  animateWidthOpen() {
    const container = this.containerElement;

    // Store current (trigger-only) width for close animation
    this.triggerOnlyWidth = container.offsetWidth;

    // Calculate target width (viewport or max 420px)
    const targetWidth = Math.min(window.innerWidth, 420);

    // Set explicit starting width
    container.style.width = `${this.triggerOnlyWidth}px`;

    // Force reflow
    container.offsetWidth;

    // Add transition and animate to target
    container.style.transition = "width 200ms ease-out";
    container.style.width = `${targetWidth}px`;

    // Clear transition after animation (keep width to prevent snap)
    container.addEventListener("transitionend", (e) => {
      if (e.propertyName === "width") {
        container.style.transition = "";
      }
    }, { once: true });
  }

  animateWidthClose() {
    const container = this.containerElement;

    // Get current width
    const currentWidth = container.offsetWidth;

    // Use stored trigger width (can't measure now - trigger has expanded)
    const targetWidth = this.triggerOnlyWidth || currentWidth;

    // Set explicit starting width
    container.style.width = `${currentWidth}px`;

    // Force reflow
    container.offsetWidth;

    // Add transition and animate to target
    container.style.transition = "width 200ms ease-out";
    container.style.width = `${targetWidth}px`;

    // Clear inline styles after animation
    container.addEventListener("transitionend", (e) => {
      if (e.propertyName === "width") {
        container.style.transition = "";
        container.style.width = "";
      }
    }, { once: true });
  }

  // No destroy() override needed — the close-button listener is on this panel's own element (and
  // the container transition/animation listeners are {once:true}), so all listeners are discarded
  // with the element. The panel is adopted by DealsWidget, so the base destroy() cascade
  // deregisters it. See .claude/skills/js-components/references/js_component_base_class.md.
}
